import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Lock, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  editableDraft,
  type Comment,
  type CommentAttachment,
  type CommentEntityType,
  type CommentFileInput,
  type CommentThread as CommentThreadData,
  type Id,
  type WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LIVE } from "@/lib/live";
import { CommentBody } from "@/modules/comments/CommentBody";
import {
  CommentComposer,
  type ComposerSubmit,
} from "@/modules/comments/CommentComposer";
import {
  dateTime,
  describeRejection,
  fileSize,
} from "@/modules/comments/format";
import { commentKeys } from "@/modules/comments/keys";
import { initials } from "@/lib/initials";

async function fileInputs(files: File[]): Promise<CommentFileInput[]> {
  return Promise.all(
    files.map(async (file) => ({
      filename: file.name,
      contentType: file.type || "application/octet-stream",
      data: new Uint8Array(await file.arrayBuffer()),
    })),
  );
}

/**
 * One file on a comment: a picture shows as a thumbnail that opens full size, a
 * PDF as a chip. Both are links to the API's own download route — the bucket is
 * private — and a picture that will not load falls back to the chip, so a file
 * is never just a broken image.
 */
function Attachment({
  attachment,
  url,
}: {
  attachment: CommentAttachment;
  url: string;
}) {
  const [broken, setBroken] = useState(false);
  const isImage = attachment.contentType.startsWith("image/");

  if (isImage && !broken) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${attachment.filename} (${fileSize(attachment.sizeBytes)})`}
        className="focus-visible:ring-ring block max-w-full overflow-hidden rounded-md border focus-visible:ring-2 focus-visible:outline-none"
      >
        <img
          src={url}
          alt={attachment.filename}
          loading="lazy"
          onError={() => {
            setBroken(true);
          }}
          className="max-h-40 max-w-full object-cover"
        />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-muted/60 hover:bg-muted focus-visible:ring-ring flex max-w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none"
    >
      <FileText className="text-muted-foreground size-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{attachment.filename}</span>
      <span className="text-muted-foreground shrink-0">
        {fileSize(attachment.sizeBytes)}
      </span>
    </a>
  );
}

function CommentItem({
  comment,
  attachmentUrl,
  people,
  canModerate,
  edit,
  remove,
}: {
  comment: Comment;
  attachmentUrl: (attachmentId: Id) => string;
  people: { id: Id; name: string }[];
  canModerate: boolean;
  edit: (
    commentId: Id,
    input: ComposerSubmit,
  ) => Promise<WriteRejection | null>;
  remove: (commentId: Id) => Promise<WriteRejection | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (comment.deletedAt !== null) {
    return (
      <li className="text-muted-foreground flex items-center gap-2 py-1 text-sm italic">
        <Trash2 className="size-3.5 shrink-0" aria-hidden />
        <span>
          A comment by {comment.authorName} was deleted ·{" "}
          {dateTime(comment.deletedAt)}
        </span>
      </li>
    );
  }

  // Not just the server's word: in a demo the signed-in permissions are the
  // persona's, so the button also follows what that persona may do.
  const canDelete = comment.canDelete && (comment.canEdit || canModerate);

  async function confirmDelete() {
    setDeleting(true);
    setProblem(null);
    try {
      const rejection = await remove(comment.id);
      if (rejection) setProblem(describeRejection(rejection));
    } catch {
      setProblem("Could not reach the server. Try again.");
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  }

  const names = new Map(
    comment.mentions.map((mention) => [mention.userId, mention.name]),
  );

  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-medium"
      >
        {initials(comment.authorName)}
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        {/* The buttons stay at the top right however the name and time wrap. */}
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 self-center">
            <span className="text-sm font-medium">{comment.authorName}</span>
            <time
              dateTime={comment.createdAt}
              className="text-muted-foreground text-xs"
            >
              {dateTime(comment.createdAt)}
            </time>
            {comment.editedAt ? (
              <span
                className="text-muted-foreground text-xs"
                title={`Edited ${comment.editedAt}`}
              >
                (edited)
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center">
            {comment.canEdit ? (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label="Edit comment"
                disabled={editing || deleting}
                onClick={() => {
                  setProblem(null);
                  setConfirming(false);
                  setEditing(true);
                }}
              >
                <Pencil />
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label="Delete comment"
                disabled={editing || deleting || confirming}
                onClick={() => {
                  setProblem(null);
                  setConfirming(true);
                }}
              >
                <Trash2 />
              </Button>
            ) : null}
          </div>
        </div>

        {editing ? (
          <CommentComposer
            people={people}
            initialDraft={editableDraft(comment.body, names)}
            allowFiles={false}
            allowEmpty={comment.attachments.length > 0}
            autoFocus
            submitLabel="Save"
            submit={async (input) => {
              const rejection = await edit(comment.id, input);
              if (!rejection) setEditing(false);
              return rejection;
            }}
            onCancel={() => {
              setEditing(false);
            }}
          />
        ) : (
          <>
            {comment.body !== "" ? (
              <CommentBody body={comment.body} mentions={comment.mentions} />
            ) : null}
            {comment.attachments.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-0.5">
                {comment.attachments.map((attachment) => (
                  <Attachment
                    key={attachment.id}
                    attachment={attachment}
                    url={attachmentUrl(attachment.id)}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}

        {confirming ? (
          <div
            role="group"
            aria-label="Confirm delete"
            className="bg-destructive/5 border-destructive/30 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm"
          >
            <span className="min-w-0 flex-1">
              Delete this comment? Its text and files are removed for good.
            </span>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={deleting}
              onClick={() => {
                void confirmDelete();
              }}
            >
              Delete
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={deleting}
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}
        {problem ? (
          <p role="alert" className="text-destructive text-sm">
            {problem}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The comments on a record (#448): one thread, shared by every kind of record
 * that has one — mount it with the record's kind and id.
 *
 * ERP-only, and says so: what is written here lives in this application and is
 * never sent to Shopify, which is why it is marked and why a note about a
 * customer's address belongs here rather than in the Shopify order note.
 *
 * Oldest first, so the newest is at the bottom beside the box that adds to it.
 * Every write is shown from what the server answered, not from what was typed,
 * and the order list's "Has comments" and "Mentions me" are refreshed — they
 * read the same threads.
 *
 * What the user may do follows the server's own flags (`canPost`, `canEdit`,
 * `canDelete`); the API checks the same rules itself, so hiding a button here
 * is a courtesy, not the control.
 */
export function CommentThread({
  entityType,
  entityId,
}: {
  entityType: CommentEntityType;
  entityId: Id;
}) {
  const data = useDataSource();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const threadKey = commentKeys.thread(entityType, entityId);

  const thread = useQuery({
    queryKey: threadKey,
    queryFn: () => data.comments.list(entityType, entityId),
  });

  const serverCanPost = thread.data?.canPost;
  const holdsCreate = can(permissions, "comments.create");
  const canPost = (serverCanPost ?? false) && holdsCreate;

  /*
    The two answers come from different times. The thread's `canPost` was worked
    out by the server for this request, from the permissions held right now;
    `permissions` is what the page last read from `/api/me`. When they disagree
    it is the page that is behind — a role was edited, or a deploy gave
    Administrator a new key, after the page loaded — and the box would stay
    missing until someone reloaded. So ask for the permissions again, now, rather
    than waiting for the tab to be refocused (#498). Settled by the next answer:
    once the two agree this does not run again, so it cannot loop.

    Not on fixtures, where a persona deliberately holds fewer permissions than
    the sample thread allows and there is nothing newer to learn.
  */
  useEffect(() => {
    if (LIVE && serverCanPost !== undefined && serverCanPost !== holdsCreate) {
      void queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    }
  }, [serverCanPost, holdsCreate, queryClient]);

  const people = useQuery({
    queryKey: commentKeys.people(entityType, entityId),
    queryFn: () => data.comments.mentionable(entityType, entityId),
    enabled: canPost,
  });

  /** Puts what the server answered into the thread, and refreshes what reads it. */
  function accepted(comment: Comment, added: boolean) {
    queryClient.setQueryData<CommentThreadData>(threadKey, (current) => {
      if (!current) return current;
      return {
        ...current,
        items: added
          ? [...current.items, comment]
          : current.items.map((item) =>
              item.id === comment.id ? comment : item,
            ),
        total: added ? current.total + 1 : current.total,
      };
    });
    // An order's page and list carry `hasComments` and `mentionsMe`.
    if (entityType === "order") {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    }
  }

  async function post(input: ComposerSubmit): Promise<WriteRejection | null> {
    const result = await data.comments.post(entityType, entityId, {
      body: input.body,
      files: await fileInputs(input.files),
    });
    if (!result.ok) return result.rejection;
    accepted(result.data, true);
    return null;
  }

  async function edit(
    commentId: Id,
    input: ComposerSubmit,
  ): Promise<WriteRejection | null> {
    const result = await data.comments.edit(
      entityType,
      entityId,
      commentId,
      input.body,
    );
    if (!result.ok) return result.rejection;
    accepted(result.data, false);
    return null;
  }

  async function remove(commentId: Id): Promise<WriteRejection | null> {
    const result = await data.comments.delete(entityType, entityId, commentId);
    if (!result.ok) return result.rejection;
    accepted(result.data, false);
    return null;
  }

  const items = thread.data?.items ?? [];
  const standing = items.filter((item) => item.deletedAt === null).length;

  return (
    <Card className="gap-3">
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-sm">
            Comments{standing > 0 ? ` (${String(standing)})` : ""}
          </CardTitle>
          <Badge variant="outline" className="gap-1 font-normal">
            <Lock aria-hidden />
            ERP only
          </Badge>
        </div>
        <p className="text-muted-foreground text-xs">
          Only in this ERP. Never sent to Shopify.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {thread.isPending ? (
          <TableSkeleton rows={2} />
        ) : thread.isError ? (
          <LoadFailed
            message={thread.error.message}
            onRetry={() => {
              void thread.refetch();
            }}
          />
        ) : (
          <>
            {thread.data.total > items.length ? (
              <p className="text-muted-foreground text-xs">
                Showing the latest {items.length} of {thread.data.total}{" "}
                comments.
              </p>
            ) : null}
            {items.length === 0 ? (
              <p className="text-muted-foreground text-sm">No comments yet.</p>
            ) : (
              <ol className="space-y-4" aria-label="Comments">
                {items.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    attachmentUrl={(attachmentId) =>
                      data.comments.attachmentUrl(
                        entityType,
                        entityId,
                        attachmentId,
                      )
                    }
                    people={people.data ?? []}
                    canModerate={can(permissions, "comments.moderate")}
                    edit={edit}
                    remove={remove}
                  />
                ))}
              </ol>
            )}

            {canPost ? (
              <CommentComposer
                people={people.data ?? []}
                allowFiles
                submitLabel="Post"
                placeholder="Write a comment"
                submit={post}
              />
            ) : (
              <p className="text-muted-foreground text-xs">
                You can read this thread but not post to it.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
